

import { useEffect, useRef, useCallback, memo, useState, useImperativeHandle, forwardRef } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { useFeira } from "../../Contexts/FeiraContext";

const Estande = memo(({ pathData, estandeInfo, isSelected, isFiltered, onClick }) => {
 const {
   corLivre = "rgba(0, 255, 0, 0.3)",
   corReservado = "rgba(255, 165, 0, 0.3)",
   corVendido = "rgba(255, 0, 0, 0.3)",
   corRenovacao = "rgba(255, 0, 0, 0.3)",
   corPendenteRenovacao = "rgba(206, 23, 184, 0.3)",
   legenda,
 } = useFeira();

 const pathRef = useRef(null);

 const obterCorDoTipoEspaco = (tipoId) => {
   const tipo = legenda.find(t => t.tipo_espaco_id === Number(tipoId));
   return tipo?.cor || "#b1b5b1ff";
 };


 let corDeFundo = corLivre;
 if (isSelected || isFiltered) {
   corDeFundo = '#FFA500';
 } else if (estandeInfo) {
   switch (estandeInfo.status_reserva_id) {
     case 2: corDeFundo = corReservado; break;
     case 3: corDeFundo = corVendido; break;
     case 5: corDeFundo = corRenovacao; break;
     case 6: corDeFundo = corPendenteRenovacao; break;
     default: corDeFundo = corLivre;
   }
 }


 const fillOpacity = estandeInfo?.permite_locacao ? 1 : 0.3;
 const fillColor = estandeInfo?.permite_locacao ? corDeFundo : obterCorDoTipoEspaco(estandeInfo?.tipo_espaco_id);


 const renderEstandeText = () => {
   if (!pathRef.current || !estandeInfo) return null;


   try {
     const bbox = pathRef.current.getBBox();
     const centerX = estandeInfo.label_pos_x ?? (bbox.x + bbox.width / 2);
     const centerY = estandeInfo.label_pos_y ?? (bbox.y + bbox.height / 2);


     const texts = [];
     let currentYOffset = -8;
     const lineSpacing = 12;


     texts.push(
       <text key="nome-estande" x={centerX} y={centerY + currentYOffset} textAnchor="middle" fontSize="20" fontWeight="bold" fill="#000" style={{ pointerEvents: 'none' }}>
         {estandeInfo.nome.toUpperCase()}
       </text>
     );
     currentYOffset += lineSpacing;


     if (estandeInfo.area > 0 && estandeInfo.permite_locacao) {
       const area = estandeInfo.area + estandeInfo.metragem_bonificada;
      
       texts.push(
         <text key="area-estande" x={centerX} y={centerY + currentYOffset} textAnchor="middle" fontSize="12" fill="#000" style={{ pointerEvents: 'none' }}>
           {`A=${parseFloat(area).toFixed(2)}m²`}
         </text>
       );
       currentYOffset += lineSpacing;
     }


     if (estandeInfo.status_reserva_id === 2 || estandeInfo.status_reserva_id === 3 || estandeInfo.status_reserva_id === 6) {
       if (estandeInfo.expositor && estandeInfo.expositor.length > 0) {
         const expositor = estandeInfo.expositor[0];
            
         const nome = (expositor.nome_mapa ?? expositor.nome.split(' ')[0]).toUpperCase();
         
         texts.push(
           <text key="nome-expositor" x={centerX} y={centerY + currentYOffset} textAnchor="middle" fontSize="9" fill="#000" style={{ pointerEvents: 'none' }}>
             {nome}
           </text>
         );
         currentYOffset += lineSpacing;
       }
       if (estandeInfo.status_reserva_id === 2) {
         texts.push(
           <text key="status-reservado" x={centerX} y={centerY + currentYOffset} textAnchor="middle" fontSize="10" fill="#000" style={{ pointerEvents: 'none' }}>
             RESERVADO
           </text>
         );
       }
     }


     return <g className="estande-label-group">{texts}</g>;
   } catch (e) {
     return null;
   }
 };

  const [bboxReady, setBboxReady] = useState(false);

  useEffect(() => {
    if (pathRef.current) setBboxReady(true);
  }, []);


 return (
   <g onClick={(e) => onClick(e, pathData, estandeInfo)}>
     <path
       ref={pathRef}
       d={pathData.path}
       fill={fillColor}
       fillOpacity={fillOpacity}
       stroke={isSelected ? "#FF0000" : "#090909ff"}
       strokeWidth={isSelected ? "2.5" : "1.28801"}
       strokeLinecap="round"
       strokeLinejoin="round"
       style={{ cursor: 'pointer', pointerEvents: 'auto' }}
       data-hash={pathData.hash}
     />
    
     {bboxReady && renderEstandeText()}
   </g>
 );
});

const MapaVisualizadorMobile = forwardRef((props, ref) => {
 const {
   mapData,
   estandesMap,
   fundoImagem,
   fundoImagemMobile,
   setSelectedEstandeInfo,
   isMobile,
   setOpenMobile,
   modoRenovacao,
   setOpenModalRenovacoes,
   setEmailRenovacao,
   estaFiltrado,
   filteredHashes,
   setEstaFiltrado,
   selectedEstandeInfo,
   corPlanoFundo = "rgba(255, 255, 255, 0.3)",
   idFeira
 } = useFeira();

 const containerRef = useRef(null);
 const svgRef = useRef(null);
 const apiRef = useRef(null);
 const [selectedHash, setSelectedHash] = useState(null);
 const [isExporting, setIsExporting] = useState(false);

 useImperativeHandle(ref, () => ({
    resetZoom: () => {
      if (apiRef.current) {
        apiRef.current.resetTransform();
      }
    },
    setZoom: (scale) => {
      if (apiRef.current) {
        const state = apiRef.current.state;
        apiRef.current.setTransform(state.positionX, state.positionY, scale, 300);
      }
    },
    resetAndZoom: (scale = 0.15) => {
      if (apiRef.current && containerRef.current) {
        const x = 200;
        const y = 10;
    
        apiRef.current.setTransform(x, y, scale, 400, "easeOut");
      }
    },
    focusOnElements: (hashes, padding = 80) => {
      return focusOnFilteredHashes(hashes, padding);
    },

    getApi: () => apiRef.current,

    exportImage: async () => {
      if (!containerRef.current || isExporting) return;
      setIsExporting(true);

      let clone;

      try {
        const vbW = mapData?.width || 1000;
        const vbH = mapData?.height || 1000;

        const targetW = 6000;
        const targetH = Math.round(targetW * (vbH / vbW));

        clone = containerRef.current.cloneNode(true);

        Object.assign(clone.style, {
          position: "absolute",
          top: "-10000px",
          left: "-10000px",
          width: `${targetW}px`,
          height: `${targetH}px`,
          display: "block",
          visibility: "visible",
          overflow: "hidden",
          background: corPlanoFundo || "#ffffff",
        });

        const spinner = clone.querySelector(".export-loading-overlay");
        if (spinner) spinner.remove();

        document.body.appendChild(clone);

        await waitForFonts();

        await inlineSvgImagesAsDataUrl(clone);

        await waitForHtmlImages(clone);

        await sleep(200);

        const canvas = await html2canvas(clone, {
          useCORS: true,
          allowTaint: false,
          backgroundColor: corPlanoFundo || "#ffffff",
          scale: 1,
          logging: false,
        });

        const link = document.createElement("a");
        link.download = `mapa-feira-${idFeira}.png`;
        link.href = canvas.toDataURL("image/png", 1.0);
        link.click();
      } catch (err) {
        console.error("Erro na exportação de imagem:", err);
      } finally {
        if (clone?.parentNode) clone.parentNode.removeChild(clone);
        setIsExporting(false);
      }
    },

    exportPDF: async () => {
  if (!containerRef.current || isExporting) return;
  setIsExporting(true);

  let clone;

  try {
    const vbW = mapData?.width || 1000;
    const vbH = mapData?.height || 1000;

    const targetW = 6000;
    const targetH = Math.round(targetW * (vbH / vbW));

    clone = containerRef.current.cloneNode(true);

    Object.assign(clone.style, {
      position: "absolute",
      top: "-10000px",
      left: "-10000px",
      width: `${targetW}px`,
      height: `${targetH}px`,
      display: "block",
      visibility: "visible",
      overflow: "hidden",
      background: corPlanoFundo || "#ffffff",
    });

    const spinner = clone.querySelector(".export-loading-overlay");
    if (spinner) spinner.remove();

    document.body.appendChild(clone);

    await waitForFonts();
    await inlineSvgImagesAsDataUrl(clone);
    await waitForHtmlImages(clone);
    await sleep(250);

    const canvas = await html2canvas(clone, {
      useCORS: true,
      allowTaint: false,
      backgroundColor: corPlanoFundo || "#ffffff",
      scale: 1,
      logging: false,
    });

    const imgData = canvas.toDataURL("image/png", 1.0);

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "px",
      format: [canvas.width, canvas.height],
    });

    pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
    pdf.save(`mapa-feira-${idFeira}.pdf`);
  } catch (err) {
    console.error("Erro ao gerar PDF:", err);
  } finally {
    if (clone?.parentNode) clone.parentNode.removeChild(clone);
    setIsExporting(false);
  }
},
 }));

 const handleEstandeClick = useCallback((event, pathData, estandeInfo) => {
   event.stopPropagation();
  
   if (!estandeInfo) return;
  
   setOpenModalRenovacoes(true);
   setSelectedHash(pathData.hash);

   if (estandeInfo.status_reserva_id === 6 && modoRenovacao) {
     setEmailRenovacao(estandeInfo.expositor[0]?.email_renovacao || "");
   }

   const valorInvalido = estandeInfo.valor_total === undefined || estandeInfo.valor_total === null || estandeInfo.valor_total < 0;
   if (valorInvalido) return;

     setSelectedEstandeInfo(estandeInfo);
     if (isMobile) setOpenMobile(true);

 }, [modoRenovacao, isMobile, setEmailRenovacao, setOpenMobile, setOpenModalRenovacoes, setSelectedEstandeInfo]);

 useEffect(() => {
   if (selectedEstandeInfo) {
     setSelectedHash(selectedEstandeInfo.hash_path);
   } else {
     setSelectedHash(null);
   }
 }, [selectedEstandeInfo]);


 useEffect(() => {
   const blockZoom = (event) => {
     if (event.ctrlKey || (event.touches && event.touches.length > 1)) {
       event.preventDefault();
     }
   };
   const container = containerRef.current;
   const options = { passive: false };
   window.addEventListener("wheel", blockZoom, options);
   window.addEventListener("touchmove", blockZoom, options);
   const allowZoomInContainer = (event) => event.stopPropagation();
   if (container) {
     container.addEventListener("wheel", allowZoomInContainer, options);
     container.addEventListener("touchmove", allowZoomInContainer, options);
   }
   return () => {
     window.removeEventListener("wheel", blockZoom);
     window.removeEventListener("touchmove", blockZoom);
     if (container) {
       container.removeEventListener("wheel", allowZoomInContainer);
       container.removeEventListener("touchmove", allowZoomInContainer);
     }
   };
 }, []);

 const focusOnFilteredHashes = useCallback(async (hashes, padding = 60, ms = 400) => {
   try {
     const api = apiRef.current;
     const svgElement = svgRef.current;
     const containerElement = containerRef.current;


     if (!hashes || hashes.length === 0 || !svgElement || !containerElement || !api) {
       return false;
     }


     const elements = [];
     hashes.forEach(hash => {
       const element = svgElement.querySelector(`path[data-hash="${hash}"]`);
       if (element) {
         elements.push(element);
       }
     });


     if (elements.length === 0) {
       return false;
     }


     let combinedBBox = null;
    
     elements.forEach(element => {
       try {
         const bbox = element.getBBox();
         if (bbox && bbox.width > 0 && bbox.height > 0) {
           if (!combinedBBox) {
             combinedBBox = { ...bbox };
           } else {
             const minX = Math.min(combinedBBox.x, bbox.x);
             const minY = Math.min(combinedBBox.y, bbox.y);
             const maxX = Math.max(combinedBBox.x + combinedBBox.width, bbox.x + bbox.width);
             const maxY = Math.max(combinedBBox.y + combinedBBox.height, bbox.y + bbox.height);
            
             combinedBBox.x = minX;
             combinedBBox.y = minY;
             combinedBBox.width = maxX - minX;
             combinedBBox.height = maxY - minY;
           }
         }
       } catch (bboxError) {
         console.warn("Erro ao calcular BBOX do elemento:", bboxError);
       }
     });


     if (!combinedBBox || combinedBBox.width <= 0 || combinedBBox.height <= 0) {
       api.resetTransform();
       return false;
     }


     const containerRect = containerElement.getBoundingClientRect();
     if (containerRect.width <= 10 || containerRect.height <= 10) {
       return false;
     }


     const scaleX = (containerRect.width - (padding * 2)) / Math.max(combinedBBox.width, 10);
     const scaleY = (containerRect.height - (padding * 2)) / Math.max(combinedBBox.height, 10);
    
     const newScale = Math.min(
       Math.max(scaleX, scaleY, 0.2),
       4 
     );

     const centerX = combinedBBox.x + combinedBBox.width / 2;
     const centerY = combinedBBox.y + combinedBBox.height / 2;


     const newPositionX = (containerRect.width / 2) - (centerX * newScale);
     const newPositionY = (containerRect.height / 2) - (centerY * newScale);


     if (!isFinite(newPositionX) || !isFinite(newPositionY) || !isFinite(newScale)) {
       api.resetTransform();
       return false;
     }
    
     api.setTransform(newPositionX, newPositionY, newScale, ms, "easeOut");
     return true;




   } catch (e) {
     console.error("[focusOnFilteredHashes] Erro crítico:", e);
     if (apiRef.current) {
       apiRef.current.resetTransform();
     }
     return false;
   }
 }, []);

 const containerStyle = {
   width: "100%",
   height: "100%",
   overflow: "hidden",
   position: "relative",
   backgroundColor: corPlanoFundo,
   touchAction: "none",
   msTouchAction: "none",
   overscrollBehavior: "contain",
   WebkitUserSelect: "none",
   userSelect: "none"
 };

 const zoomControlsStyle = {
   position: "fixed",
   right: "10px",
   bottom: "calc(var(--map-bottom-safe-area, 0px) + 16px)",
   zIndex: 1002,
   display: "flex",
   flexDirection: "column",
   gap: "8px",
   background: "rgba(255,255,255,0.9)",
   borderRadius: "20px",
   padding: "8px",
   boxShadow: "0 2px 5px rgba(0,0,0,0.1)"
 };

 const zoomButtonStyle = {
   width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(0,0,0,0.7)',
   color: 'white', border: 'none', fontSize: '16px', display: 'flex', alignItems: 'center',
   justifyContent: 'center', cursor: 'pointer'
 };

 return (
   <div ref={containerRef} style={containerStyle}>
     <TransformWrapper
       onInit={(instance) => {
           apiRef.current = instance;
           instance.centerView();
       }}
       minScale={1} 
       maxScale={20}
       initialScale={1}
       limitToBounds={true}           
       centerZoomedOut={true}         
       disabled={false}
       alignmentAnimation={{ disabled: true }} 
       wheel={{ step: 0.1 }}
       doubleClick={{ disabled: true }}
       panning={{ velocityDisabled: true }} 
       style={{ width: "100%", height: "100%" }}
     >
       {({ zoomIn, zoomOut, resetTransform, centerView }) => (
         <>
            <TransformComponent
              wrapperStyle={{
                width: "100%",
                height: "100%",
                touchAction: "none",
                msTouchAction: "none",
                overscrollBehavior: "contain"
              }}
              contentStyle={{
                width: "100%",
                height: "100%",
                touchAction: "none",
                msTouchAction: "none"
              }}
             >
             <svg
               ref={svgRef}
               id="mapa"
               viewBox={`0 0 ${mapData?.width || 1000} ${mapData?.height || 1000}`}
               style={{
                width: '100%',
                height: '100%',
                touchAction: 'none',
                msTouchAction: 'none',
                display: 'block',
              }}
             >
               {fundoImagem && (
                 <g id="fundo">
                   <image href={fundoImagemMobile} x="0" y="0" width={mapData?.width || 1000} height={mapData?.height || 1000} crossOrigin="anonymous" style={{ pointerEvents: 'none' }} />
                 </g>
               )}
               <g id="camada">
                 {mapData?.paths && Object.values(mapData.paths).map((pathData) => {
                   const estandeInfo = estandesMap.get(pathData.hash);
                   const isSelected = pathData.hash === selectedHash;
                   const isFiltered = estaFiltrado && filteredHashes.includes(pathData.hash);

                   return (
                     <Estande
                       key={pathData.hash}
                       pathData={pathData}
                       estandeInfo={estandeInfo}
                       isSelected={isSelected}
                       isFiltered={isFiltered}
                       onClick={handleEstandeClick}
                     />
                   );
                 })}
               </g>
             </svg>
           </TransformComponent>
           <div style={zoomControlsStyle}>
             <button onClick={() => zoomIn()} style={zoomButtonStyle}>+</button>
             <button onClick={() => resetTransform()} style={zoomButtonStyle} title="Centralizar Mapa">⟲</button>
             <button onClick={() => zoomOut()} style={zoomButtonStyle}>−</button>
             {estaFiltrado && (
               <button
                 onClick={() => {
                   setEstaFiltrado(false);
                   resetTransform();
                 }}
                 style={zoomButtonStyle}
                 title="Voltar à visualização geral"
               >
                 ⌂
               </button>
             )}
           </div>
         </>
       )}
     </TransformWrapper>
   </div>
 );
});

export default memo(MapaVisualizadorMobile);